import { useState, useEffect } from "react";

import toast from "components/partials/toast/toast";

import { isApiError } from "config/axios-utils";

import { Unpersisted } from "types/utility";

import {
  useReplaceTouchpointVersion,
  type TouchpointVersionAttributes,
} from "hooks/api/use-touchpoint-versions";
import { useApplyBrandStyles } from "hooks/api/use-bee";
import { getTouchpointVersion } from "generated/api/touchpoint-version";
import { mapTouchpointVersionDTO } from "utilities/api-mappers";

import { normalizeValues } from "utilities/forms";

import { LoadingButtonState } from "ui/button";

const touchpointVersionApi = getTouchpointVersion();

interface TouchpointPreviewData {
  existingDesignHtml?: string;
  existingDesignBeeJson?: string;
  themeUpdatedHtml?: string;
  themeUpdatedBeeJson?: string;
}

interface UseThemeUpdatePreviewProps {
  touchpointVersionId: string;
  isOpen: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  clientId: string;
  campaignId: string;
  touchpointId: string;
}

export const useUpdateClientTheme = ({
  touchpointVersionId,
  isOpen,
  setIsOpen = () => {},
  clientId,
  campaignId,
  touchpointId,
}: UseThemeUpdatePreviewProps) => {
  const versionScope = { clientId, campaignId, touchpointId };
  const replaceVersionMutation = useReplaceTouchpointVersion(versionScope);
  const applyBrandStylesMutation = useApplyBrandStyles();
  const [touchpointData, setTouchpointData] = useState<TouchpointPreviewData>({});
  const [currentVersion, setCurrentVersion] =
    useState<TouchpointVersionAttributes | null>(null);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);
  const [isLoadingThemed, setIsLoadingThemed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [existingDesignError, setExistingDesignError] = useState<string | null>(null);
  const [themeUpdatedError, setThemeUpdatedError] = useState<string | null>(null);
  const [isRetryableError, setIsRetryableError] = useState(false);
  const [buttonState, setButtonState] = useState<LoadingButtonState>(LoadingButtonState.Default);

  const fetchTouchpointData = async (): Promise<TouchpointPreviewData> => {
    if (!touchpointVersionId) {
      setIsLoadingExisting(false);
      setIsLoadingThemed(false);
      return {};
    }

    setExistingDesignError(null);
    setThemeUpdatedError(null);

    // Fetch original touchpoint data
    let versionData: TouchpointVersionAttributes | undefined;
    try {
      const dto = await touchpointVersionApi.getOneVersion({
        clientId,
        campaignId,
        touchpointId,
        id: touchpointVersionId,
      });
      versionData = mapTouchpointVersionDTO(dto);
    } catch (error) {
      console.error("Failed to fetch touchpoint version:", error);
      setExistingDesignError("Failed to load creative preview.");
      setIsLoadingExisting(false);
      setIsLoadingThemed(false);
      return {};
    }

    if (!versionData) {
      setIsLoadingExisting(false);
      setExistingDesignError("Failed to load creative preview");
      return {};
    }

    setCurrentVersion(versionData);

    const existingDesignData = {
      existingDesignHtml: versionData.finalHtml,
      existingDesignBeeJson: versionData.data?.jsonOutput,
    };

    setTouchpointData(existingDesignData);
    setIsLoadingExisting(false);

    // Apply brand styles using the touchpoint version ID
    const themeUpdatedData = await fetchThemeUpdatedTouchpoint(touchpointVersionId);

    return {
      ...existingDesignData,
      ...themeUpdatedData,
    };
  };

  // Apply theme to the touchpoint data
  const fetchThemeUpdatedTouchpoint = async (
    touchpointVersionId: string,
  ): Promise<{ themeUpdatedHtml?: string; themeUpdatedBeeJson?: string }> => {
    try {
      const response = await applyBrandStylesMutation.mutateAsync({ touchpointVersionId });

      // Clear error state on success
      setThemeUpdatedError(null);
      setIsRetryableError(false);

      return {
        themeUpdatedHtml: response.htmlOutput,
        themeUpdatedBeeJson: response.jsonOutput,
      };
    } catch (error) {
      console.error("Failed to fetch styled touchpoint data:", error);

      // Check if error is retryable by checking the errorKey in the response
      let isRetryable = false;
      if (isApiError(error)) {
        const errorData = error.response?.data as Record<string, unknown> | undefined;
        const errorKey = errorData?.errorKey || "";
        // 5xx errors are retryable
        isRetryable = typeof errorKey === "string" && errorKey === "http.5xx";
      }

      setIsRetryableError(isRetryable);
      setThemeUpdatedError("Failed to apply theme");

      return {};
    }
  };

  // Update selected touchpoint version with new themed content
  const handleApplyTheme = async () => {
    if (
      !touchpointData.themeUpdatedHtml ||
      !touchpointData.themeUpdatedBeeJson ||
      !currentVersion
    ) {
      return;
    }

    setIsSaving(true);
    setButtonState(LoadingButtonState.Loading);
    try {
      // Combine existing touchpoint attributes with new themed content
      // Only update the data field with new themed content, keep all other fields
      const updatedAttributes: Unpersisted<TouchpointVersionAttributes> = {
        ...normalizeValues(currentVersion),
        data: {
          htmlOutput: touchpointData.themeUpdatedHtml,
          jsonOutput: touchpointData.themeUpdatedBeeJson,
        },
        sourceType: "BEE",
      } as Unpersisted<TouchpointVersionAttributes>;

      await replaceVersionMutation.mutateAsync({
        id: touchpointVersionId,
        attributes: updatedAttributes,
      });

      // Show success message
      toast.success({
        data: {
          title: "Theme applied",
        },
      });

      // Set button to success state
      setButtonState(LoadingButtonState.Success);

      // Close modal on success
      setIsOpen(false);
    } catch (error) {
      console.error("Failed to apply theme", error);
      toast.error({
        data: {
          title: "Failed to apply theme",
        },
      });
      setButtonState(LoadingButtonState.Error);
    } finally {
      setIsSaving(false);
    }
  };

  // Retry function to re-fetch styled touchpoint data
  const handleRetry = async () => {
    if (!touchpointVersionId) {
      return;
    }

    setIsLoadingThemed(true);
    setIsRetryableError(false);

    try {
      const themeUpdatedData = await fetchThemeUpdatedTouchpoint(touchpointVersionId);
      setTouchpointData((prev) => ({
        ...prev,
        ...themeUpdatedData,
      }));
    } catch (error) {
      console.error("Retry failed:", error);
    } finally {
      setIsLoadingThemed(false);
    }
  };

  // Set loading states immediately when modal opens
  useEffect(() => {
    if (isOpen) {
      // Clear data immediately when modal opens
      setTouchpointData({});
      setCurrentVersion(null);
      setExistingDesignError(null);
      setThemeUpdatedError(null);
      setIsRetryableError(false);
      setButtonState(LoadingButtonState.Default);
      // Set both loading states to true immediately so skeleton loaders show right away
      setIsLoadingExisting(true);
      setIsLoadingThemed(true);
    } else {
      // Reset loading states when modal closes
      setIsLoadingExisting(false);
      setIsLoadingThemed(false);
    }
  }, [isOpen]);

  // Load touchpoint data when modal opens
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const loadData = async () => {
      const data = await fetchTouchpointData();
      setTouchpointData(data);
      setIsLoadingThemed(false);
    };

    loadData();
    // eslint-disable-next-line
  }, [isOpen, touchpointVersionId, clientId, campaignId, touchpointId]);

  return {
    // State
    touchpointData,
    currentTouchpointVersion: currentVersion,
    isLoadingExisting,
    isLoadingThemed,
    isSaving,
    existingDesignError,
    themeUpdatedError,
    isRetryableError,
    buttonState,
    // Actions
    handleApplyTheme,
    handleRetry,
  };
};
